import React, { useEffect, useState } from 'react';
import { AxiosError, AxiosResponse } from 'axios';

import { useDispatch } from 'react-redux';
import { mainMenuTypes, IMenuItem } from '@app/components/Sidebar/menuObject';
import { getMenuItemById } from '@app/helpers';
import { PageLoader, PasslySwitch } from '@app/components';
import { useSetPage, useTypedTranslation } from '@app/hooks';
import { basicService, kaseyaOneService } from '@app/services';
import { handleSetSnackbar } from '@app/store/actions/snackbar.actions';
import { TranslationsTypes } from '@app/models';
import { Typography } from '@material-ui/core';
import { PasslyTooltip } from '@app/componentsV2';

const RETURN_ENABLE_STATE = 'k1_enable_organization';
const RETURN_DISABLE_STATE = 'k1_disable_organization';
export enum SwitcherTypeEnum {
    integration = 'integration',
    login = 'login'
}

const KaseyaOnePage = () => {
    const { translation } = useTypedTranslation();
    const item: IMenuItem | undefined = getMenuItemById(mainMenuTypes.KaseyaOne);
    const [isIntegrationChecked, setIsIntegrationChecked] = useState(false);
    const [isLoginChecked, setIsLoginChecked] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isShowLoader, setIsShowLoader] = useState(false);
    const queryCodeString = basicService.getQueryVariable('code');
    const queryStateString = basicService.getQueryVariable('state');
    const queryErrorString = basicService.getQueryVariable('error');

    const dispatch = useDispatch();
    const orgEnableSuccess = () => {
        setIsIntegrationChecked(true);
        kaseyaOneService.getKaseyaOneLoginStatus(handleError, handleGetLoginStatusSuccess);
        dispatch(handleSetSnackbar({ show: true, message: translation('KASEYA_ONE_IS_ENABLED'), severity: 'success' }));
    };

    const orgDisableSuccess = () => {
        setIsShowLoader(false);
        setIsIntegrationChecked(false);
        setIsLoginChecked(false);
        dispatch(handleSetSnackbar({ show: true, message: translation('KASEYA_ONE_IS_DISABLED'), severity: 'success' }));
    };

    const handleSwitchSuccess = () => {
        setIsShowLoader(false);
    };

    const handleSwitchError = () => {
        setIsShowLoader(false);
        setIsLoginChecked(false);
    };

    const handleGetRedirectUrlSuccess = (response: AxiosResponse) => {
        const result: any = response.data;

        setIsShowLoader(false);

        if (result && result.url) {
            window.location.href = result.url;
        }
    };

    const handleError = (error: AxiosError<any>) => {
        setIsShowLoader(false);
        setErrorMessage(error.response?.data.error_description || translation(error.response?.data.error.toUpperCase()));
    };

    const handleSwitchChange = (name: string) => () => {
        switch (name) {
            case SwitcherTypeEnum.integration:
                if (isIntegrationChecked) {
                    setIsIntegrationChecked(!isIntegrationChecked);
                    kaseyaOneService.getRedirectOrgDisableUrl(handleError, handleGetRedirectUrlSuccess);
                } else {
                    setIsShowLoader(true);
                    kaseyaOneService.getRedirectOrgUrl(handleError, handleGetRedirectUrlSuccess);
                }
                break;

            case SwitcherTypeEnum.login:
                setIsLoginChecked(!isLoginChecked);
                setIsShowLoader(true);
                kaseyaOneService.switchLoginKaseyaOneButton({ enabled: !isLoginChecked }, handleSwitchError, handleSwitchSuccess);
                break;

            default:
                break;
        }
    };

    const handleGetIntegrationStatusSuccess = (response: AxiosResponse) => {
        const result: any = response.data;

        if (result) {
            const integrationEnabled = result.enabled;
            setIsIntegrationChecked(integrationEnabled);
            if (integrationEnabled) {
                kaseyaOneService.getKaseyaOneLoginStatus(handleError, handleGetLoginStatusSuccess);
            } else {
                setIsLoginChecked(false);
            }
        }
    };

    const handleGetLoginStatusSuccess = (response: AxiosResponse) => {
        const result: any = response.data;

        if (result) {
            setIsLoginChecked(result.enabled);
        }

        setIsShowLoader(false);
    };

    useSetPage(null, item);

    useEffect(() => {
        kaseyaOneService.getKaseyaOneIntegrationStatus(handleError, handleGetIntegrationStatusSuccess);
    }, []);

    useEffect(() => {
        if (queryStateString === RETURN_ENABLE_STATE && (queryCodeString || queryErrorString)) {
            const data = queryCodeString ? { code: queryCodeString } : { error: queryErrorString };

            setIsShowLoader(true);
            kaseyaOneService.enableTenantKaseyaOne(data, handleError, orgEnableSuccess);
        }

        if (queryStateString === RETURN_DISABLE_STATE && (queryCodeString || queryErrorString)) {
            const data = queryCodeString ? { code: queryCodeString } : { error: queryErrorString };

            setIsShowLoader(true);
            kaseyaOneService.disableTenantKaseyaOne(data, handleError, orgDisableSuccess);
        }
    }, []);

    useEffect(() => {
        if (queryErrorString) {
            const errorMessage = translation(queryErrorString as TranslationsTypes);

            dispatch(handleSetSnackbar({ show: true, message: errorMessage, severity: 'error' }));
        }
    }, [queryErrorString]);

    return (
        <div className='kaseya-one'>
            <main className='content'>
                <h1>{translation('KASEYA_ONE')}</h1>

                <div className='kaseya-one__block'>
                    <h3>{translation('KASEYA_ONE_INTEGRATION_TITLE')}</h3>
                    <div className='kaseya-one__switch-text'>
                        {translation('KASEYA_ONE_SWITCHER_LABEL')}
                        <PasslyTooltip title={translation('KASEYA_ONE_INTEGRATION_TOOLTIP')}>
                            <span>
                                <PasslySwitch checked={isIntegrationChecked} label='' onChange={handleSwitchChange('integration')} />
                            </span>
                        </PasslyTooltip>
                    </div>
                    <Typography className='kaseya-one__help-text' variant='subtitle1'>
                        {translation('KASEYA_ONE_INTEGRATION_DESCRIPTION')}
                    </Typography>
                </div>
                <div className='kaseya-one__block'>
                    <h3>{translation('KASEYA_ONE_K1_LOGIN_TITLE')}</h3>
                    <div className='kaseya-one__switch-text'>
                        {translation('KASEYA_ONE_SWITCHER_LABEL')}
                        <PasslyTooltip title={translation('KASEYA_ONE_K1_LOGIN_TOOLTIP')}>
                            <span>
                                <PasslySwitch checked={isLoginChecked} label='' onChange={handleSwitchChange('login')} disabled={!isIntegrationChecked || isShowLoader} />
                            </span>
                        </PasslyTooltip>
                    </div>
                    <Typography className='kaseya-one__help-text' variant='subtitle1'>
                        {translation('KASEYA_ONE_K1_LOGIN_DESCRIPTION')}
                    </Typography>
                </div>
                {errorMessage && <div className='kaseya-one__error'>{errorMessage}</div>}
            </main>
            {isShowLoader && <PageLoader useOverlay={true} />}
        </div>
    );
};

export default KaseyaOnePage;
